import { useMemo, type CSSProperties, type ReactNode } from "react";
import QRCode from "qrcode";
import type { PosterElement, PosterImageSource, PosterShapeElement } from "@studio/shared";
import { FLOWER_CENTER, IMAGE_BORDER_MM, ROUND_RADIUS_MM, SHAPE_STROKE_MM, STICKER_PATHS } from "./art";
import { cssWeight, posterFont } from "./fonts";
import { MM_PER_PT, type TextLayout } from "./geometry";

/**
 * 포스터 요소 하나를 DOM 으로 그린다 — 미리보기·편집기·교사 진행판이 같이 쓴다.
 * 좌표는 mm 이고 `scale`(px/mm)을 곱해 그린다. 글상자 줄은 `layout`(geometry.layoutText) 그대로 그린다.
 */

export type ImageResolver = (source: PosterImageSource) => string | null;

/** QR 모듈 행렬 — 편집 화면 SVG·PNG·PDF 가 같은 행렬을 쓴다(오류정정 M · 키링과 같다). */
export function qrModules(url: string): boolean[][] {
  const code = QRCode.create(url, { errorCorrectionLevel: "M" });
  const size = code.modules.size;
  return Array.from({ length: size }, (_, y) =>
    Array.from({ length: size }, (_, x) => Boolean(code.modules.get(x, y)))
  );
}

/** QR 바깥 여백(모듈 수) — 인식기는 흰 테두리가 있어야 읽는다 */
export const QR_QUIET = 2;

export function QrSvg({ url }: { url: string | null }) {
  const modules = useMemo(() => qrModules(url ?? "https://studio.teachermate.co.kr"), [url]);
  const size = modules.length + QR_QUIET * 2;
  const path = modules.flatMap((row, y) => row.flatMap((on, x) =>
    on ? [`M${x + QR_QUIET} ${y + QR_QUIET}h1v1h-1z`] : []
  )).join("");
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" shapeRendering="crispEdges" aria-hidden="true" style={{ display: "block", background: "#fff" }}>
      <path d={path} fill="#000" />
    </svg>
  );
}

function shapeSvg(element: PosterShapeElement, scale: number): ReactNode {
  const stroke = element.stroke === "none" ? "none" : element.stroke;
  const strokeWidth = element.stroke === "none" ? 0 : SHAPE_STROKE_MM * scale;
  if (element.kind === "circle") {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
        <ellipse cx="50" cy="50" rx="50" ry="50" fill={element.fill} stroke={stroke} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  if (element.kind === "triangle") {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
        <path d="M50 0 100 100H0z" fill={element.fill} stroke={stroke} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  const radius = element.kind === "pill" ? 9999 : element.kind === "round" ? ROUND_RADIUS_MM * scale : 0;
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: element.fill,
        borderRadius: radius,
        boxSizing: "border-box",
        border: strokeWidth ? `${strokeWidth}px solid ${stroke}` : undefined,
      }}
    />
  );
}

export function PosterElementView({
  element,
  scale,
  layout,
  resolveImage,
  qrUrl,
  className,
  children,
  dataId = true,
}: {
  element: PosterElement;
  scale: number;
  layout?: TextLayout | undefined;
  resolveImage: ImageResolver;
  qrUrl: string | null;
  className?: string;
  children?: ReactNode;
  dataId?: boolean;
}) {
  const height = element.type === "text" ? (layout?.height ?? 10) : element.h;
  const style: CSSProperties = {
    position: "absolute",
    left: element.x * scale,
    top: element.y * scale,
    width: element.w * scale,
    height: height * scale,
    transform: element.rot ? `rotate(${element.rot}deg)` : undefined,
    opacity: element.opacity,
  };
  let body: ReactNode = null;
  if (element.type === "text" && layout) {
    const font = posterFont(element.font);
    body = layout.lines.map((line, index) => (
      <div
        key={index}
        style={{
          height: layout.lineHeight * scale,
          lineHeight: `${layout.lineHeight * scale}px`,
          fontFamily: `"${font.family}"`,
          fontWeight: cssWeight(font, element.weight),
          fontSize: element.size * MM_PER_PT * scale,
          color: element.color,
          textAlign: element.align,
          whiteSpace: "pre",
        }}
      >{line || " "}</div>
    ));
  } else if (element.type === "image") {
    const url = resolveImage(element.src);
    const radius = element.shape === "circle" ? "50%" : element.shape === "round" ? ROUND_RADIUS_MM * scale : 0;
    body = url ? (
      <img
        src={url}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          borderRadius: radius,
          boxShadow: element.border !== "none" ? `0 0 0 ${IMAGE_BORDER_MM * scale}px ${element.border}` : undefined,
        }}
      />
    ) : (
      <div className="poster-el-missing" style={{ borderRadius: radius }}>그림 자리</div>
    );
  } else if (element.type === "shape") {
    body = shapeSvg(element, scale);
  } else if (element.type === "sticker") {
    body = (
      <svg viewBox="0 0 100 100" width="100%" height="100%" fill={element.color}>
        <path d={STICKER_PATHS[element.kind]} fillRule="nonzero" />
        {element.kind === "flower" ? <circle {...FLOWER_CENTER} fill="#fff" fillOpacity={0.85} /> : null}
      </svg>
    );
  } else if (element.type === "qr") {
    body = <QrSvg url={qrUrl} />;
  }
  return (
    <div className={className} style={style} data-id={dataId ? element.id : undefined}>
      {body}
      {children}
    </div>
  );
}
